import React, { useContext, useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { SaveFormButton, CancelFormButton } from '../../components/';
import { gql, useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';
import getModalContext from '../../context/getModalContext';
import useNotifier from '../../hooks/useNotifier';

interface IModalValues {
  password: string;
  confirmPassword: string;
}

interface IProps {
  show: boolean;
  account: any;
}

const PasswordModal: React.FC<IProps> = ({ show, account }) => {
  const modals = useContext(getModalContext());
  const notifier = useNotifier();
  const [updating, setUpdating] = useState<boolean>(false);
  const {
    register,
    handleSubmit,
    reset: resetForm,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<IModalValues>();
  const formHasChanges = isDirty && Object.keys(dirtyFields).length > 0;

  const [updateUser] = useMutation(UPDATE_ACCOUNT_USER, {
    onCompleted: () => {
      setUpdating(false);
      modals?.setModalState('passwordModal', false);
      notifier.showMessage(
        'Successfully saved!',
        'Your account password has been updated',
        'success'
      );
    },
  });

  const onSubmit = (values: IModalValues) => {
    console.log(account);
    setUpdating(true);
    updateUser({
      variables: {
        accountId: account.id,
        userId: account.user.id,
        user: {
          password: values.password,
        },
      },
    });
  };

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-10 inset-0 overflow-y-auto"
        // initialFocus={purchaseButtonRef}
        onClose={() => modals?.setModalState('passwordModal', false)}
      >
        <div
          className="flex min-h-screen text-center sm:block sm:px-6 lg:px-8"
          style={{ fontSize: 0 }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="hidden sm:block sm:fixed sm:inset-0 sm:bg-gray-500 sm:bg-opacity-75 sm:transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-105"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-105"
          >
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle max-w-sm sm:w-full sm:p-6">
              <form onSubmit={handleSubmit(onSubmit)}>
                <div>
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                    {/* Heroicon name: outline/check */}
                    <svg
                      className="h-6 w-6 text-green-600"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                      />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-5">
                    <h3
                      className="text-lg leading-6 font-medium text-gray-900"
                      id="modal-title"
                    >
                      Setup your password
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Choose a strong password you will remember for your new
                        account.
                      </p>
                    </div>
                  </div>
                  <div>
                    <div className="mt-6 mb-10 mx-4 space-y-6">
                      <div>
                        <label
                          htmlFor="password"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Password
                        </label>
                        <div className="mt-1">
                          <input
                            id="setPassword"
                            type="password"
                            autoComplete="new-password"
                            required
                            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-light focus:border-primary-light sm:text-sm"
                            {...register('password')}
                          />
                        </div>
                      </div>
                      <div>
                        <label
                          htmlFor="confirmPassword"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Confirm Password
                        </label>
                        <div className="mt-1">
                          <input
                            id="confirmPassword"
                            type="password"
                            autoComplete="new-password"
                            required
                            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-light focus:border-primary-light sm:text-sm"
                            {...register('confirmPassword')}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 flex items-center justify-end">
                  <SaveFormButton loading={updating} label="Save Password" />
                </div>
              </form>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default PasswordModal;

const UPDATE_ACCOUNT_USER = gql`
  mutation AccountPasswordModal_UpdateAccountUser(
    $accountId: String!
    $userId: String!
    $user: UserInput!
  ) {
    accountUserUpdate(accountId: $accountId, userId: $userId, user: $user) {
      id
      scope
      state {
        status
        setAt
      }
      roles
      user {
        id
        username
        state {
          status
          setAt
        }
        roles
        scope
        person {
          name {
            first
            last
          }
          email
        }
      }
      users {
        id
        username
        state {
          status
          setAt
        }
        roles
        scope
        person {
          name {
            first
            last
          }
          email
        }
      }
    }
  }
`;
